/**
 * CpLogButton — compacte inline log-knop voor Timeline-entries.
 *
 * Klik → opent een mini-formulier direct onder de entry (geen modal).
 * Na versturen toont de knop "Gelogd ✓" en blijft dat ook na een herlaad
 * (via localStorage). Bij netwerkverlies: "In wachtrij" — kalm, geen paniek.
 */
import { useState } from "react";
import { sendCpLog, markLogged, isLogged } from "../lib/cplog";
import { getMelderString } from "../lib/firestore-ops";

// PM26-partijen uit de plannen: CSS (Kevin, PM beveiliging), SKILLS (Alien, CSM),
// EMS (medisch), TTS (verkeer, alleen via coördinator), organisatie, stagemanager.
const SNELKEUZE = [
  "CSS/Kevin",
  "SKILLS/Alien",
  "EMS",
  "TTS-coörd",
  "Organisatie",
  "Stagemanager",
] as const;

type Status = "idle" | "open" | "sending" | "logged" | "queued";

interface Props {
  entryId: string;
  defaultTijd: string;
  defaultBericht: string;
}

export function CpLogButton({ entryId, defaultTijd, defaultBericht }: Props) {
  const [status, setStatus] = useState<Status>(() =>
    isLogged(entryId) ? "logged" : "idle",
  );
  const [bericht, setBericht] = useState(defaultBericht);
  const [uitgezetNaar, setUitgezetNaar] = useState("");
  const [afhandeling, setAfhandeling] = useState("");

  const melder = getMelderString();
  const sending = status === "sending";

  async function submit() {
    if (!bericht.trim()) return;
    setStatus("sending");
    try {
      await sendCpLog({ tijd: defaultTijd, melder, bericht, uitgezetNaar, afhandeling });
      markLogged(entryId);
      setStatus("logged");
    } catch {
      setStatus("queued");
    }
  }

  if (status === "logged") {
    return (
      <span className="text-2xs text-ok mt-1.5 inline-block">
        Gelogd ✓
      </span>
    );
  }

  if (status === "queued") {
    return (
      <span
        className="text-2xs text-zinc-400 mt-1.5 inline-block cursor-default"
        title="Geen verbinding — de log wordt verstuurd zodra de verbinding terugkeert."
      >
        In wachtrij ⏳
      </span>
    );
  }

  if (status === "idle") {
    return (
      <button
        type="button"
        onClick={() => setStatus("open")}
        className="mt-1.5 text-2xs border border-line text-zinc-500 hover:text-zinc-300 hover:border-zinc-600 px-2 py-0.5 rounded min-h-[28px] transition-colors"
      >
        Log
      </button>
    );
  }

  // status === "open" | "sending"
  return (
    <div className="mt-2 pt-2 border-t border-line space-y-2">
      {/* Vaste velden: tijd en melder (read-only) */}
      <div className="flex gap-4 text-2xs text-zinc-500">
        <span>
          Tijd:{" "}
          <span className="text-zinc-300 tabular">{defaultTijd}</span>
        </span>
        <span>
          Melder:{" "}
          <span className="text-zinc-300">{melder}</span>
        </span>
      </div>

      {/* Bericht */}
      <div>
        <label className="text-2xs text-zinc-500 block mb-1">Bericht</label>
        <textarea
          rows={2}
          value={bericht}
          onChange={(e) => setBericht(e.target.value)}
          disabled={sending}
          className="w-full bg-ink-900 border border-line rounded-lg px-2 py-1.5 text-xs text-zinc-200 resize-none leading-relaxed focus:outline-none focus:border-zinc-600 disabled:opacity-50"
        />
      </div>

      {/* Uitgezet naar — snelkeuze + vrij veld */}
      <div>
        <label className="text-2xs text-zinc-500 block mb-1">
          Uitgezet naar
        </label>
        <div className="flex flex-wrap gap-1 mb-1.5">
          {SNELKEUZE.map((chip) => (
            <button
              key={chip}
              type="button"
              disabled={sending}
              onClick={() =>
                setUitgezetNaar((prev) => (prev === chip ? "" : chip))
              }
              className={`text-2xs px-2 py-0.5 rounded-full border transition-colors min-h-[24px] disabled:opacity-50 ${
                uitgezetNaar === chip
                  ? "bg-accent/20 border-accent/40 text-accent"
                  : "border-line text-zinc-400 hover:text-zinc-200 hover:border-zinc-600"
              }`}
            >
              {chip}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="of typ een naam"
          value={uitgezetNaar}
          onChange={(e) => setUitgezetNaar(e.target.value)}
          disabled={sending}
          className="w-full bg-ink-900 border border-line rounded-lg px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-zinc-600 disabled:opacity-50"
        />
      </div>

      {/* Afhandeling */}
      <div>
        <label className="text-2xs text-zinc-500 block mb-1">Afhandeling</label>
        <input
          type="text"
          placeholder="loopt / afgerond / ter info"
          value={afhandeling}
          onChange={(e) => setAfhandeling(e.target.value)}
          disabled={sending}
          className="w-full bg-ink-900 border border-line rounded-lg px-2 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-zinc-600 disabled:opacity-50"
        />
      </div>

      {/* Acties */}
      <div className="flex items-center justify-end gap-2 pt-0.5">
        <button
          type="button"
          onClick={() => setStatus("idle")}
          disabled={sending}
          className="text-2xs text-zinc-400 hover:text-zinc-200 px-2 py-1 rounded min-h-[32px] disabled:opacity-40"
        >
          Annuleer
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={sending || !bericht.trim()}
          className="text-2xs bg-accent/15 border border-accent/40 text-accent hover:bg-accent/25 px-3 py-1 rounded min-h-[32px] disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? "Bezig..." : "Verstuur"}
        </button>
      </div>
    </div>
  );
}
